import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Divider, TextField } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DialogType, SelectOption } from '../../../@types';
import { BonusCampaign } from '../../../@types/api';
import {
  BONUS_ELIGIBILITY_OPTIONS,
  BONUS_STATUS_OPTIONS_CUSTOM,
  BONUS_STATUS_OPTIONS_ONGOING,
  BONUS_TIMEFRAME_OPTIONS,
  QUERY_KEYS,
} from '../../../constants';
import { DEFAULT_CASHBACK_FORM_DATA } from '../../../constants/defaultFormValues';
import API_ROUTES from '../../../constants/apiRoutes';
import { FEATURE_FLAG } from '../../../constants/featureFlags';
import { useModal } from '../../../contexts/ModalContext';
import { getCasinoFreeSpinsAggregator } from '../../../helpers/casino';
import useMutateData from '../../../hooks/useMutateData';
import { useCasinoMarketplace } from '../../../queries/casino';
import { CashbackBonusFormData, CashbackBonusFormValues, cashbackBonusFormSchema } from '../../../schema';
import {
  buildCashbackBonusCreatePayload,
  buildCashbackBonusEditFormValues,
  buildCashbackBonusUpdatePayload,
  getCashbackBonusDefaultProduct,
  getCashbackBonusProductOptions,
} from '../../../utils/cashbackBonus';
import FormModalLayout from '../../layouts/FormModalLayout';
import CustomerSelect from '../../molecules/CustomerSelect';
import FormSelect from '../../molecules/FormSelect';
import ValidityPeriodSelect from '../../molecules/ValidityPeriodSelect';
import CashbackRewardFields from './CashbackRewardFields';
import CashbackRewardTypeSelect from './CashbackRewardTypeSelect';

type ValidityPeriod = CashbackBonusFormValues['validityPeriod'];
type BonusStatus = CashbackBonusFormValues['status'];

const STATUS_CONFIG: Record<
  ValidityPeriod,
  {
    defaultStatus: BonusStatus;
    statusOptions: readonly SelectOption[];
  }
> = {
  CUSTOM: {
    defaultStatus: 'SCHEDULED',
    statusOptions: BONUS_STATUS_OPTIONS_CUSTOM,
  },
  ONGOING: {
    defaultStatus: 'READY',
    statusOptions: BONUS_STATUS_OPTIONS_ONGOING,
  },
};

type Props = DialogType;

const CashbackBonusForm = ({ closeModal }: Props) => {
  const { item } = useModal<BonusCampaign>();
  const { createData, updateData } = useMutateData(
    API_ROUTES.CASHBACK_BONUS_CAMPAIGNS,
    [QUERY_KEYS.cashbackBonus],
    'wallet'
  );

  const isEditMode = !!item;
  const editFormValues = useMemo(() => (item ? buildCashbackBonusEditFormValues(item) : undefined), [item]);
  const isCasinoCashbackEnabled = useFeatureIsOn(FEATURE_FLAG.CASINO_CASHBACK_BONUS);
  const { data: casinoMarketplace } = useCasinoMarketplace({ enabled: isCasinoCashbackEnabled });
  const selectedAggregator = getCasinoFreeSpinsAggregator(casinoMarketplace);
  const freeSpinsAggregator =
    selectedAggregator === 'GGA' || selectedAggregator === 'GOLDGAMES' ? selectedAggregator : undefined;
  const isFreeSpinsEnabled = !!freeSpinsAggregator;
  const productOptions = getCashbackBonusProductOptions(isCasinoCashbackEnabled, item?.product);
  const defaultFormValues = useMemo<CashbackBonusFormValues>(
    () => ({
      ...DEFAULT_CASHBACK_FORM_DATA,
      product: getCashbackBonusDefaultProduct(isCasinoCashbackEnabled),
    }),
    [isCasinoCashbackEnabled]
  );

  const form = useForm<CashbackBonusFormValues, unknown, CashbackBonusFormData>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(cashbackBonusFormSchema),
  });
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
    watch,
    setValue,
  } = form;

  const validityPeriod = watch('validityPeriod');
  const product = watch('product');
  const { defaultStatus, statusOptions } = STATUS_CONFIG[validityPeriod];

  useEffect(() => {
    if (!isEditMode) {
      setValue('status', defaultStatus);
    }
  }, [defaultStatus, isEditMode, setValue]);

  useEffect(() => {
    if (!isEditMode && !isCasinoCashbackEnabled && product === 'CASINO') {
      setValue('product', 'SPORTSBOOK');
    }
  }, [isCasinoCashbackEnabled, isEditMode, product, setValue]);

  const handleClose = () => {
    closeModal?.();
    reset(defaultFormValues);
  };

  const onFormSubmit = (data: CashbackBonusFormData) => {
    if (item) {
      const updatePayload = buildCashbackBonusUpdatePayload(data, dirtyFields);
      updateData(item.id, updatePayload, handleClose);
    } else {
      createData(buildCashbackBonusCreatePayload(data), handleClose);
    }
  };

  useEffect(() => {
    if (editFormValues) {
      reset(editFormValues);
    }
  }, [editFormValues, reset]);

  const userIds = watch('userIds');

  return (
    <FormProvider {...form}>
      <FormModalLayout
        onSave={handleSubmit(onFormSubmit)}
        label={isEditMode ? 'Edit bonus' : 'Add new bonus'}
        onClose={handleClose}
        isEdit={isEditMode}
        showEditTabs={false}
      >
        <TextField
          error={!!errors.name}
          helperText={errors.name?.message}
          label='Name'
          {...register('name')}
          required
          autoFocus
        />
        <FormSelect
          label='Product'
          name='product'
          control={control}
          error={errors.product}
          options={productOptions}
          required
          allowDeselect={false}
          disabled={isEditMode}
        />
        <FormSelect
          label='Eligibility criteria'
          name='eligibility'
          control={control}
          error={errors.eligibility}
          options={BONUS_ELIGIBILITY_OPTIONS}
          required
          allowDeselect={false}
          disabled={isEditMode}
        />
        <CashbackRewardTypeSelect
          existingRewardType={item?.rewardType}
          isEditMode={isEditMode}
          isFreeSpinsEnabled={isFreeSpinsEnabled}
        />
        <Divider />
        <ValidityPeriodSelect
          control={control}
          disabled={isEditMode}
          fromTimestampError={errors.fromTimestamp}
          required
          toTimestampError={errors.toTimestamp}
        />
        <FormSelect
          control={control}
          name='status'
          label='Status'
          defaultValue={defaultStatus}
          error={errors.status}
          options={statusOptions}
          required
          allowDeselect={false}
          disabled={watch('validityPeriod') === 'CUSTOM' && !isEditMode}
        />
        <Divider />
        <CashbackRewardFields
          disabled={isEditMode}
          aggregator={isEditMode ? editFormValues?.freeSpinTiers[0]?.aggregator : freeSpinsAggregator}
          marketplaceId={casinoMarketplace?._id}
          currency={casinoMarketplace?.currency}
        />
        <FormSelect
          label='Timeframe'
          name='schedule'
          control={control}
          error={errors.schedule}
          options={BONUS_TIMEFRAME_OPTIONS}
          required
          allowDeselect={false}
          disabled={isEditMode}
        />
        <CustomerSelect
          control={control}
          name='userIds'
          label='Applied to'
          placeholder={!!userIds.length ? '' : 'All players'}
          slotProps={{ inputLabel: { shrink: true } }}
          disabled={isEditMode}
        />
        <TextField
          error={!!errors.description}
          helperText={errors.description?.message}
          label='Description'
          {...register('description')}
          multiline
        />
      </FormModalLayout>
    </FormProvider>
  );
};

export default CashbackBonusForm;
